import { Component, Prop } from "@stencil/core";
import ky from "ky";
import io, { Socket } from "socket.io-client";
import dayjs from "dayjs";
import { keys, isEmpty, values, isNil } from "lodash-es";
import {
  ILiftStatusSensorResponse,
  ILiftStatusSensorDataResponse,
  ILiftStatusModuleStatus,
  TLiftStatusWebComponentErrorType,
  TObjectType,
  ILiftStatusAuth,
  ILiftStatusValues,
  ILiftStatusUpdated,
} from "../../utils/types";
import {
  createNewStore,
  getStore,
  ILiftStatusState,
  updateStore,
} from "../../store";
import { ObservableMap } from "@stencil/store";

@Component({
  tag: "ls-data",
  shadow: true,
})
export class LsData {
  /**
   * The token used to connect to the Socket.IO and API servers.
   */
  @Prop() readonly accessToken: string;

  /**
   * The unique key that is used to identify store data.
   */
  @Prop() readonly idKey: string;

  /**
   * Extra sensor IDs that should be included in the API requests.
   */
  @Prop() readonly sensorIds: string;

  private store: ObservableMap<ILiftStatusState> | null = null;

  /**
   * The list of relevant sensors that is used when performing API requests.
   */
  private readonly defaultSensorIds = [
    104, 105, 106, 210, 500, 501, 502, 503, 513, 514, 515, 550, 590, 591
  ];

  /**
   * The ID of the connection with the socket server.
   * Currently not in use. Can be used in the future to distinguish between multiple installations.
   */
  private socketId: string;

  async componentWillLoad() {
    // Check for a valid access token and if so decode it
    const { valid, objectId, objectType } = await this.decodeAccessToken(
      this.accessToken
    );
    if (!valid) return;

    // Check if the installation has a back door
    const hasBackDoorPromise = this.checkHasBackdoor(
      objectType,
      objectId,
      this.accessToken
    );

    const mergedSensorIds = [
      // Remove duplicates using a Set
      ...new Set(
        this.defaultSensorIds.concat(
          this.sensorIds !== undefined
            ? this.sensorIds.split(",").map(Number)
            : []
        )
      ),
    ];

    // Get the historical data from the past hour
    const historicalDataPastHourPromise = this.getHistoricalData(
      dayjs().subtract(1, "hour").format(),
      dayjs().format(),
      mergedSensorIds,
      objectType,
      objectId,
      this.accessToken
    );

    // Connect to the socket server
    const connectedPromise = this.connectToSocketServer(this.accessToken);

    // We stored all the promises, so now we can fire them concurrently
    const [hasBackDoor, historicalDataPastHour, { connected, socket }] =
      await Promise.all([
        hasBackDoorPromise,
        historicalDataPastHourPromise,
        connectedPromise,
      ]);

    // If something went wrong an error should already be thrown, so simply return here
    if (isNil(hasBackDoor) || isNil(historicalDataPastHour) || !connected) {
      return;
    }

    // We always need a key to identify the store data, so if no key is provided we create one ourselves
    const idKey = this.idKey ?? `${objectType}-${objectId}`;

    // Save state in store
    createNewStore(idKey, {
      hasBackDoor,
      historicalSensorData: historicalDataPastHour,
      currentSensorData: this.getCurrentDataFromHistorical(
        historicalDataPastHour
      ),
    });

    try {
      this.store = await getStore(idKey);
    } catch (error) {
      this.throwError(
        "Something went wrong while creating the store",
        "Token",
        error
      );
    }

    // Listen to future realtime updates
    this.listenForSensorUpdates(socket, idKey, mergedSensorIds);
  }

  /**
   * Checks for valid access token and decodes it
   * @param accessToken
   * @returns \{ valid: true, objectId, objectType \} if the token is valid
   * @returns \{ valid: false \}  if the token is invalid
   */
  private async decodeAccessToken(
    accessToken: string
  ): Promise<{ valid: boolean; objectId?: string; objectType?: string }> {
    if (isEmpty(accessToken)) {
      this.throwError(
        "No Access token supplied",
        "Token",
        "Add the token to the <ls-data> component like so: <ls-data access-token='yourToken'></ls-data>"
      );
      return {
        valid: false,
      };
    }

    try {
      const response: ILiftStatusAuth = await ky
        .post("https://api.liftstatus.nl/liftstatus/auth", {
          headers: { Authorization: `Bearer ${accessToken}` },
          json: { accessToken },
          throwHttpErrors: true,
        })
        .json();

      const objectId = response.tokenData.oid;
      let objectType: TObjectType;

      switch (response.tokenData.otype) {
        case "int":
          objectType = "internal";
          break;
        case "inst":
          objectType = "installation";
          break;
        case "ext":
          objectType = "external";
          break;
      }

      return {
        valid: true,
        objectId,
        objectType,
      };
    } catch (error) {
      this.throwError("Access token is invalid", "Token", error.message);
      return {
        valid: false,
      };
    }
  }

  /**
   * This function checks if the elevator has a back door.
   * It does this by checking for activity on sensor 517 for the past week.
   */
  private async checkHasBackdoor(
    objectType: string,
    objectId: string,
    accessToken: string
  ) {
    const fromTS = dayjs().subtract(7, "day").format();

    try {
      const response: ILiftStatusSensorResponse = await ky
        .get(
          `https://api.liftstatus.nl/liftstatus/${objectType}/${objectId}/sensor/517/summary`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            searchParams: { from: fromTS },
            throwHttpErrors: true,
            timeout: 60000,
          }
        )
        .json();

      const start = response.rows[0]?.start;
      const end = response.rows[0]?.end;

      return end - start === 0 ? false : true;
    } catch (error) {
      this.throwError("Checking for back door failed", "API", error.message);
    }
  }

  /**
   * This function retrieves historical data from the API server for a specified amount of time.
   * @param {string} fromTS A formatted Day.js object.
   * @param {string} toTS A formatted Day.js object.
   */
  private async getHistoricalData(
    fromTS: string,
    toTS: string,
    sensorList: number[],
    objectType: string,
    objectId: string,
    accessToken: string
  ) {
    try {
      const response: ILiftStatusSensorResponse[] = await ky
        .post(
          `https://api.liftstatus.nl/liftstatus/${objectType}/${objectId}/sensor`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            searchParams: {
              from: fromTS,
              to: toTS,
              sensors: sensorList.join(),
            },
            throwHttpErrors: true,
            timeout: 60000,
          }
        )
        .json();

      return response;
    } catch (error) {
      this.throwError("Failed to get historical data", "API", error.message);
    }
  }

  private connectToSocketServer(
    accessToken: string
  ): Promise<{ connected: boolean; socket?: SocketIOClient.Socket }> {
    // Close any existing connections
    if (!isEmpty(this.socketId)) {
      this.closeSocketConnection();
    }

    return new Promise((resolve) => {
      const socket = io("https://api.liftstatus.nl/", {
        transports: ["websocket"],
        path: "/liftstatus/socket.io",
        reconnectionAttempts: 5,
        query: {
          access_token: `${accessToken}`,
        },
      });

      // Wait until the io function returns a connection
      socket.on("connect", () => {
        resolve({
          connected: true,
          socket,
        });
      });

      // Listen for any errors
      socket.on("error", (error: string) => {
        this.throwError(
          "Failed to connect to socket server",
          "Socket",
          error,
          socket
        );
        resolve({ connected: false });
      });
    });
  }

  private listenForSensorUpdates(
    socket: SocketIOClient.Socket,
    idKey: string,
    sensorIds: number[]
  ) {
    // Responds with live updates from the socket server
    socket.on("module", ({ message }: ILiftStatusModuleStatus) => {
      // Update data values and timestamp
      const data = message.data;
      const dataKey = keys(data)[0];
      const dataValue = values(data)[0];
      const timestamp = message.ts;

      // Check if the sensor id is in the list of sensors we want to listen to
      if (!sensorIds.includes(parseInt(dataKey, 10))) return;

      // Overwrite only the updated values
      updateStore(
        {
          lastUpdate: timestamp,
          values: {
            ...this.store.state.currentSensorData.values,
            [dataKey]: dataValue,
          },
          updated: {
            ...this.store.state.currentSensorData.updated,
            [dataKey]: timestamp,
          },
        },
        idKey
      );
    });

    // Listen to socket server updates (this triggers the onModule updates)
    socket.emit("listen", {});
  }

  private getCurrentDataFromHistorical(
    historicalData: ILiftStatusSensorResponse[]
  ): ILiftStatusSensorDataResponse {
    let lastUpdate: number = 0;
    let values: ILiftStatusValues = {};
    let updated: ILiftStatusUpdated = {};

    historicalData.forEach((sensorRow) => {
      const sensorId = sensorRow.sensor.id;
      const lastRow = sensorRow.rows[sensorRow.rows.length - 1];
      const lastRowTsIsMostRecent = dayjs(lastRow.ts).isAfter(
        dayjs(lastUpdate)
      );

      // lastRow.val is always of type string, so we need to check if we can parse it to a number
      const lastRowTypeIsNumber = isFinite(parseInt(lastRow.val, 10));

      if (lastRowTsIsMostRecent) {
        lastUpdate = dayjs(lastRow.ts).valueOf();
      }

      values = {
        ...values,
        [sensorId]: lastRowTypeIsNumber
          ? parseInt(lastRow.val, 10)
          : lastRow.val,
      };

      updated = {
        ...updated,
        [sensorId]: dayjs(lastRow.ts).valueOf(),
      };
    });

    return {
      lastUpdate,
      values,
      updated,
    };
  }

  private async throwError(
    errorMessage: string,
    errorType: TLiftStatusWebComponentErrorType,
    errorDescription?: string,
    additionalInfo?: any
  ) {
    // Log the error for the user
    console.group(
      `%cError%c [LsData] ${errorMessage}`,
      "color: #DC2626; background-color: #FEF2F2; display: inline-block; padding: 0 4px; border-radius: 2px;",
      ""
    );

    errorDescription && console.log(errorDescription);
    additionalInfo && console.log(additionalInfo);

    console.groupEnd();

    if (isNil(this.store)) {
      // We always need a key to identify the store data, so if no key is provided we create one ourselves
      // Normally we use a combination of the objectType and objectId as fallback, but since this is an error we can't do that
      createNewStore(this.idKey ?? "error", {});
      this.store = await getStore(this.idKey);
    }

    // Update the state with the error
    this.store.state.error = { message: errorMessage, type: errorType };
  }

  private closeSocketConnection() {
    Socket.close();
    this.socketId = null;
  }

  disconnectedCallback() {
    this.closeSocketConnection();
  }
}
